import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import CountdownPill from "@/components/CountdownTimer";
import { Radio, PlusCircle, Trash2, Pencil, X, Upload } from "lucide-react";

const PRESET_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free"];

// Same GST slab suggestion rules as the single product upload form.
function suggestGstSlab(categorySlug, mrpValue, catsList) {
  const catObj = (catsList || []).find((c) => c.slug === categorySlug);
  const catName = (catObj?.name || categorySlug || "").toLowerCase();
  const slug = (categorySlug || "").toLowerCase();
  const key = `${slug} ${catName}`;
  const mrp = Number(mrpValue) || 0;
  const label = catObj?.name || "this category";
  if (/(kid|chaniya|choli|fashion|clothing|apparel|dress|footwear|shoe|saree|kurta|ethnic)/.test(key)) {
    if (mrp > 0 && mrp < 1000) return { slab: 5, reason: `Suggested 5% — ${label} priced below ₹1000.` };
    if (mrp >= 1000) return { slab: 12, reason: `Suggested 12% — ${label} priced ₹1000 or above.` };
    return { slab: 5, reason: `Suggested 5% by default. Rate becomes 12% once MRP reaches ₹1000 or more.` };
  }
  if (/(accessor|jewel|bag|belt|watch)/.test(key)) {
    return { slab: 12, reason: `Suggested 12% — accessories attract a flat 12% GST.` };
  }
  if (/(handmade|handicraft|artisan|handloom)/.test(key)) {
    return { slab: 5, reason: `Suggested 5% — handmade and handicraft goods.` };
  }
  if (/(toy)/.test(key)) {
    return { slab: 12, reason: `Suggested 12% — toys.` };
  }
  const fallback = catObj?.gst ?? 18;
  return { slab: fallback, reason: `Suggested ${fallback}% based on the ${label} default rate.` };
}

const emptyItem = {
  name: "", description: "", mrp: "", discount: "", category: "", images: [],
  stock: 10, sizes: [], color: "", material: "", specifications: "",
  gst_slab: 5, hsn_code: "", packaging_charge: 0,
  weight_kg: "", length_cm: "", breadth_cm: "", height_cm: "",
  delivery_within_city: true, delivery_within_state: true, delivery_out_of_state: true,
  cod_available: true,
};

// 4:5 portrait cover-fit, same as the single product upload (1200 x 1500).
const readImg = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const TW = 1200, TH = 1500;
    const scale = Math.max(TW / img.width, TH / img.height);
    const w = img.width * scale, h = img.height * scale;
    const c = document.createElement("canvas");
    c.width = TW; c.height = TH;
    c.getContext("2d").drawImage(img, (TW - w) / 2, (TH - h) / 2, w, h);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject;
  img.src = url;
});

function ImagePicker({ images, onChange, testid }) {
  const add = async (files) => {
    const room = 6 - images.length;
    const picked = Array.from(files || []).slice(0, room);
    if (!picked.length) return;
    try {
      const out = [];
      for (const f of picked) out.push(await readImg(f));
      onChange([...images, ...out]);
    } catch { toast.error("Could not read an image"); }
  };
  return (
    <div className="space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testid}-img-thumbs`}>
          {images.map((src, idx) => (
            <div key={idx} className="relative">
              <img src={src} className="h-16 w-16 object-cover rounded-md border border-gray-200" alt="" />
              {idx === 0 && <span className="absolute -top-1.5 -left-1.5 text-[9px] bg-[#F48E2E] text-white px-1.5 py-0.5 rounded-full">Cover</span>}
              <button type="button" data-testid={`${testid}-img-rm-${idx}`} onClick={() => onChange(images.filter((_, j) => j !== idx))}
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] flex items-center justify-center hover:bg-red-600" aria-label={`Remove image ${idx + 1}`}>×</button>
            </div>
          ))}
        </div>
      )}
      {images.length < 6 && (
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-xl hover:border-[#F48E2E] w-fit">
          <Upload className="h-4 w-4" />
          <span className="text-sm">Add image{images.length > 0 ? `s (${6 - images.length} left)` : "s (up to 6)"}</span>
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testid}-img-input`} />
        </label>
      )}
      <p className="text-[11px] text-gray-500">Up to 6 photos from different angles. First image becomes the marketplace thumbnail.</p>
    </div>
  );
}

function ProductForm({ form, setForm, testid, cats, vendorCat, keepSavedGst = false }) {
  // Edit mode starts "overridden" so the saved GST rate is not replaced by the suggestion on open.
  const [gstOverridden, setGstOverridden] = useState(keepSavedGst);
  const gstSuggestion = suggestGstSlab(form.category || vendorCat, form.mrp, cats);

  useEffect(() => {
    if (gstOverridden) return;
    if (Number(form.gst_slab) !== gstSuggestion.slab) setForm((f) => ({ ...f, gst_slab: gstSuggestion.slab }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category, form.mrp, gstOverridden]);

  const catOptions = vendorCat
    ? [{ slug: vendorCat, name: (cats.find((c) => c.slug === vendorCat) || {}).name || vendorCat }, ...cats.filter((c) => c.parent_slug === vendorCat)]
    : cats;

  const sellingPrice = form.mrp ? Math.round((Number(form.mrp) - (Number(form.mrp) * Number(form.discount || 0)) / 100) * 100) / 100 : null;

  return (
    <div className="space-y-3">
      <div>
        <Label>Product Images <span className="text-gray-400 font-normal">(up to 6 — first is the cover)</span></Label>
        <div className="mt-1"><ImagePicker testid={testid} images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} /></div>
      </div>

      <div><Label>Product Name <span className="text-red-500">*</span></Label><Input data-testid={`${testid}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>

      <div>
        <Label>Category <span className="text-red-500">*</span></Label>
        <Select value={form.category || vendorCat || ""} onValueChange={(v) => { setGstOverridden(false); setForm({ ...form, category: v }); }}>
          <SelectTrigger data-testid={`${testid}-category`}><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>{catOptions.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div><Label>Product Description</Label><Textarea data-testid={`${testid}-desc`} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>HSN Code <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Input data-testid={`${testid}-hsn`} placeholder="e.g. 6204" inputMode="numeric" maxLength={8} value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 8) })} />
        </div>
        <div>
          <Label>Packaging charge (₹) <span className="text-gray-400 font-normal">per unit</span></Label>
          <Input data-testid={`${testid}-packaging`} type="number" min="0" step="1" placeholder="0" value={form.packaging_charge} onChange={(e) => setForm({ ...form, packaging_charge: Math.max(0, Number(e.target.value) || 0) })} />
        </div>
        <div>
          <Label>GST Rate <span className="text-red-500">*</span></Label>
          <Select value={String(form.gst_slab)} onValueChange={(v) => { setGstOverridden(true); setForm({ ...form, gst_slab: Number(v) }); }}>
            <SelectTrigger data-testid={`${testid}-gst`}><SelectValue placeholder="Select GST rate" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0% (exempt)</SelectItem>
              <SelectItem value="5">5%</SelectItem>
              <SelectItem value="12">12%</SelectItem>
              <SelectItem value="18">18%</SelectItem>
              <SelectItem value="28">28%</SelectItem>
            </SelectContent>
          </Select>
          <p data-testid={`${testid}-gst-hint`} className="mt-1 text-[11px] leading-snug text-gray-500">
            {gstSuggestion.reason}
            {gstOverridden && Number(form.gst_slab) !== gstSuggestion.slab && (
              <> <button type="button" data-testid={`${testid}-gst-apply`} className="underline text-[#F48E2E] hover:text-[#d97a1f]"
                onClick={() => { setGstOverridden(false); setForm({ ...form, gst_slab: gstSuggestion.slab }); }}>Apply {gstSuggestion.slab}%</button></>
            )}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-gray-200 p-3">
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1">Shipping dimensions <span className="text-gray-400 font-normal normal-case tracking-normal">(optional — defaults to 0.5 kg · 20×15×10 cm)</span></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><Label className="text-[11px]">Weight (kg)</Label><Input data-testid={`${testid}-weight`} type="number" min="0" step="0.01" placeholder="0.5" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="h-9" /></div>
          <div><Label className="text-[11px]">Length (cm)</Label><Input data-testid={`${testid}-length`} type="number" min="0" step="0.5" placeholder="20" value={form.length_cm} onChange={(e) => setForm({ ...form, length_cm: e.target.value })} className="h-9" /></div>
          <div><Label className="text-[11px]">Breadth (cm)</Label><Input data-testid={`${testid}-breadth`} type="number" min="0" step="0.5" placeholder="15" value={form.breadth_cm} onChange={(e) => setForm({ ...form, breadth_cm: e.target.value })} className="h-9" /></div>
          <div><Label className="text-[11px]">Height (cm)</Label><Input data-testid={`${testid}-height`} type="number" min="0" step="0.5" placeholder="10" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="h-9" /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>MRP (₹) <span className="text-gray-400 font-normal">excl. GST</span></Label>
          <Input data-testid={`${testid}-mrp`} type="number" min="0" placeholder="e.g. 500" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} />
        </div>
        <div>
          <Label>Discount (%) <span className="text-gray-400 font-normal">optional</span></Label>
          <Input data-testid={`${testid}-discount`} type="number" min="0" max="100" placeholder="0 to 100" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
        </div>
        <div className="col-span-2 -mt-1">
          <div className="rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30 px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Selling price shown to buyers (excl. GST)</span>
              <span className="font-mono-tab text-sm font-semibold text-[#111827]">{sellingPrice != null ? `₹${sellingPrice.toLocaleString("en-IN")}` : "Enter MRP above"}</span>
            </div>
          </div>
        </div>
        <div><Label>Stock Quantity</Label><Input data-testid={`${testid}-stock`} type="number" min="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
      </div>

      <div>
        <Label>Available Sizes</Label>
        <div className="mt-1 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {PRESET_SIZES.map((s) => {
              const active = (form.sizes || []).includes(s);
              return (
                <button key={s} type="button" data-testid={`${testid}-size-${s}`}
                  onClick={() => setForm({ ...form, sizes: active ? form.sizes.filter((x) => x !== s) : [...(form.sizes || []), s] })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#F48E2E] text-white border-[#F48E2E]" : "bg-white text-gray-600 border-gray-200 hover:border-[#F48E2E] hover:text-[#F48E2E]"}`}>
                  {s}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input data-testid={`${testid}-size-custom`} placeholder="Custom size (e.g. 24 x 36 in)" value={form._customSize || ""}
              onChange={(e) => setForm({ ...form, _customSize: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = (form._customSize || "").trim();
                  if (v && !(form.sizes || []).includes(v)) setForm({ ...form, sizes: [...(form.sizes || []), v], _customSize: "" });
                }
              }}
              className="h-9 text-sm" />
            <Button type="button" data-testid={`${testid}-size-custom-add`} variant="outline" className="h-9 rounded-full text-xs whitespace-nowrap"
              onClick={() => {
                const v = (form._customSize || "").trim();
                if (v && !(form.sizes || []).includes(v)) setForm({ ...form, sizes: [...(form.sizes || []), v], _customSize: "" });
              }}>Add</Button>
          </div>
          {(form.sizes || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {form.sizes.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FEF3E6] text-[#B65C1A] border border-[#F48E2E]/40">
                  {s}
                  <button type="button" onClick={() => setForm({ ...form, sizes: form.sizes.filter((x) => x !== s) })} className="text-[#B65C1A] hover:text-red-600" aria-label={`Remove ${s}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label>Colour</Label><Input data-testid={`${testid}-color`} placeholder="Rani pink / Royal blue" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
        <div><Label>Material</Label><Input data-testid={`${testid}-material`} placeholder="Cotton / Silk / Brass" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} /></div>
        <div className="col-span-2"><Label>Specifications</Label><Textarea data-testid={`${testid}-specs`} placeholder="Wash care, dimensions, handmade details, etc." value={form.specifications} onChange={(e) => setForm({ ...form, specifications: e.target.value })} /></div>
      </div>

      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Shipping Settings</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
            <Checkbox data-testid={`${testid}-delivery-city`} checked={form.delivery_within_city} onCheckedChange={(v) => setForm({ ...form, delivery_within_city: !!v })} className="mt-0.5" />
            <div><div className="text-sm font-medium">Within city</div><div className="text-[11px] text-gray-500">1 day</div></div>
          </label>
          <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
            <Checkbox data-testid={`${testid}-delivery-state`} checked={form.delivery_within_state} onCheckedChange={(v) => setForm({ ...form, delivery_within_state: !!v })} className="mt-0.5" />
            <div><div className="text-sm font-medium">Within state</div><div className="text-[11px] text-gray-500">2-3 days</div></div>
          </label>
          <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
            <Checkbox data-testid={`${testid}-delivery-outstate`} checked={form.delivery_out_of_state} onCheckedChange={(v) => setForm({ ...form, delivery_out_of_state: !!v })} className="mt-0.5" />
            <div><div className="text-sm font-medium">Out of state</div><div className="text-[11px] text-gray-500">3-5 days</div></div>
          </label>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Payment Options</div>
        <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 bg-white cursor-pointer">
          <Checkbox data-testid={`${testid}-cod`} checked={form.cod_available} onCheckedChange={(v) => setForm({ ...form, cod_available: !!v })} className="mt-0.5" />
          <div><div className="text-sm font-medium">Cash on Delivery available</div><div className="text-[11px] text-gray-500">Uncheck to make this product Prepaid only (online payment).</div></div>
        </label>
      </div>
    </div>
  );
}

function StatusPill({ status, id }) {
  const styles = {
    draft: "bg-gray-100 text-gray-600 border-gray-200",
    live: "bg-green-50 text-green-700 border-green-200",
    expired: "bg-gray-100 text-gray-400 border-gray-200",
  };
  return <span data-testid={`batch-status-${id}`} className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${styles[status] || styles.draft}`}>{status}</span>;
}

export default function VendorLiveTab({ onChanged }) {
  const [batches, setBatches] = useState([]);
  const [cats, setCats] = useState([]);
  const [vendorCat, setVendorCat] = useState("");
  const [addFor, setAddFor] = useState(null);
  const [item, setItem] = useState(emptyItem);
  const [hoursMap, setHoursMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyItem);

  const load = async () => {
    try { const { data } = await api.get("/exhibitor/live-batches"); setBatches(data || []); }
    catch { toast.error("Could not load live batches"); }
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : (r.data.categories || []))).catch(() => {});
    api.get("/exhibitor/profile").then((r) => setVendorCat(r.data?.kyc?.business_category || "")).catch(() => {});
  }, []);

  const newBatch = async () => {
    try { await api.post("/exhibitor/live-batches"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create batch"); }
  };

  const validateItem = (f) => {
    if (!f.images?.length) return "Please upload at least one product image (up to 6)";
    if (!f.name?.trim()) return "Please enter the product name";
    if (f.hsn_code && !/^\d{4,8}$/.test(f.hsn_code)) return "HSN Code must be 4 to 8 digits";
    if (![0, 5, 12, 18, 28].includes(Number(f.gst_slab))) return "Please select a GST Rate";
    const mrp = Number(f.mrp);
    if (!mrp || mrp <= 0) return "Please enter a valid MRP";
    if (!(Number(f.stock) >= 1)) return "Stock must be at least 1";
    return null;
  };

  const itemPayload = (f) => {
    const mrp = Number(f.mrp);
    let discount = Number(f.discount || 0);
    if (discount < 0) discount = 0;
    if (discount > 100) discount = 100;
    const price = Math.round((mrp - (mrp * discount) / 100) * 100) / 100;
    return {
      name: f.name.trim(), description: f.description, category: f.category || vendorCat,
      price, original_price: mrp, discount_percent: discount,
      stock: Number(f.stock), images: f.images,
      gst_slab: Number(f.gst_slab), hsn_code: (f.hsn_code || "").trim(),
      packaging_charge: Number(f.packaging_charge || 0),
      weight_kg: f.weight_kg === "" ? null : Number(f.weight_kg),
      length_cm: f.length_cm === "" ? null : Number(f.length_cm),
      breadth_cm: f.breadth_cm === "" ? null : Number(f.breadth_cm),
      height_cm: f.height_cm === "" ? null : Number(f.height_cm),
      sizes: f.sizes || [], color: f.color, material: f.material, specifications: f.specifications,
      delivery_within_city: !!f.delivery_within_city,
      delivery_within_state: !!f.delivery_within_state,
      delivery_out_of_state: !!f.delivery_out_of_state,
      cod_available: !!f.cod_available,
    };
  };

  const addProduct = async (batchId) => {
    const err = validateItem(item);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      await api.post(`/exhibitor/live-batches/${batchId}/products`, itemPayload(item));
      toast.success("Product added to batch (hidden until Start Live)");
      setItem(emptyItem); setAddFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add product"); }
    finally { setBusy(false); }
  };

  const openEdit = (batchId, p) => {
    setEditing({ batchId, product: p });
    setEditForm({
      ...emptyItem,
      name: p.name, description: p.description || "", mrp: p.original_price ?? p.price, discount: p.discount_percent || 0,
      category: p.category || "", stock: p.stock, images: p.images || (p.image_base64 ? [p.image_base64] : []),
      gst_slab: p.gst_slab ?? 18, hsn_code: p.hsn_code || "", packaging_charge: p.packaging_charge || 0,
      weight_kg: p.weight_kg ?? "", length_cm: p.length_cm ?? "", breadth_cm: p.breadth_cm ?? "", height_cm: p.height_cm ?? "",
      sizes: p.sizes || [], color: p.color || "", material: p.material || "", specifications: p.specifications || "",
      delivery_within_city: p.delivery_within_city !== false,
      delivery_within_state: p.delivery_within_state !== false,
      delivery_out_of_state: p.delivery_out_of_state !== false,
      cod_available: p.cod_available !== false,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const err = validateItem(editForm);
    if (err) return toast.error(err);
    try {
      await api.patch(`/exhibitor/live-batches/${editing.batchId}/products/${editing.product.id}`, itemPayload(editForm));
      toast.success("Product updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update product"); }
  };

  const removeProduct = async (batchId, p) => {
    if (!window.confirm(`Remove "${p.name}" from this batch?`)) return;
    try { await api.delete(`/exhibitor/live-batches/${batchId}/products/${p.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove product"); }
  };

  const startLive = async (b) => {
    const raw = String(hoursMap[b.id] ?? 48).trim();
    const h = Number(raw);
    if (raw === "" || !Number.isInteger(h) || h < 0 || h > 240) return toast.error("Enter a whole number of hours between 0 and 240");
    if (h === 0 && !window.confirm("0 hours will make this batch expire immediately and it cannot be restarted. Continue?")) return;
    setBusy(true);
    try {
      await api.post(`/exhibitor/live-batches/${b.id}/start`, { duration_hours: h });
      toast.success("Batch is LIVE — all products are now visible with a shared timer");
      load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start live"); }
    finally { setBusy(false); }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm("Delete this draft batch and its products?")) return;
    try { await api.delete(`/exhibitor/live-batches/${b.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete batch"); }
  };

  // Rendered as a plain function (not a nested component) so React never remounts
  // the subtree mid-typing — nested component identity was the focus-loss bug.
  const renderBatchCard = (b) => {
    const draft = b.status === "draft";
    const live = b.status === "live";
    return (
      <div key={b.id} data-testid={`batch-${b.id}`} className={`p-5 bg-white border rounded-2xl ${live ? "border-green-300" : "border-gray-200"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={b.status} id={b.id} />
            <span className="text-xs text-gray-500">{b.product_count} product{b.product_count === 1 ? "" : "s"}{live ? ` · ${b.active_product_count} active` : ""}</span>
            {b.starts_at && <span className="text-[11px] text-gray-400">Start {new Date(b.starts_at).toLocaleString("en-IN")} · Expiry {new Date(b.expires_at).toLocaleString("en-IN")}</span>}
          </div>
          {live && <CountdownPill targetIso={b.expires_at} />}
          {draft && (
            <div className="flex items-center gap-2 flex-wrap">
              <Input data-testid={`batch-hours-${b.id}`} type="number" min="0" max="240" value={hoursMap[b.id] ?? 48} onChange={(e) => setHoursMap({ ...hoursMap, [b.id]: e.target.value })} className="h-9 w-24 text-xs" title="Duration in hours (0–240)" />
              <Button data-testid={`batch-start-${b.id}`} onClick={() => startLive(b)} disabled={busy || b.product_count === 0} className="rounded-full bg-red-600 hover:bg-red-700 text-white h-9">
                <Radio className="h-3.5 w-3.5 mr-1.5" /> Start Live
              </Button>
              <Button data-testid={`batch-delete-${b.id}`} variant="outline" onClick={() => deleteBatch(b)} className="rounded-full h-9 text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          )}
        </div>

        {(b.products || []).length > 0 && (
          <div className="space-y-2 mb-3">
            {b.products.map((p) => (
              <div key={p.id} data-testid={`batch-product-${p.id}`} className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 bg-gray-50/60">
                {p.image_base64 ? <img src={p.image_base64} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-orange-50" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[11px] text-gray-500">₹{Number(p.price).toLocaleString("en-IN")} · stock {p.stock}{live && (p.stock <= 0 || !p.visible) ? " · not showing" : ""}</div>
                </div>
                {(draft || live) && (
                  <button type="button" data-testid={`batch-product-edit-${p.id}`} onClick={() => openEdit(b.id, p)} className="p-1.5 text-gray-500 hover:text-[#F48E2E]"><Pencil className="h-4 w-4" /></button>
                )}
                {draft && (
                  <button type="button" data-testid={`batch-product-remove-${p.id}`} onClick={() => removeProduct(b.id, p)} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}

        {draft && (
          addFor === b.id ? (
            <div className="p-4 border border-dashed border-[#F48E2E] rounded-xl bg-[#FEF3E6]/40">
              <ProductForm form={item} setForm={setItem} testid={`batch-add-${b.id}`} cats={cats} vendorCat={vendorCat} />
              <div className="flex gap-2 mt-3">
                <Button data-testid={`batch-add-save-${b.id}`} onClick={() => addProduct(b.id)} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-9">{busy ? "Adding…" : "Add to batch"}</Button>
                <Button variant="outline" onClick={() => { setAddFor(null); setItem(emptyItem); }} className="rounded-full h-9">Cancel</Button>
              </div>
            </div>
          ) : (
            <Button data-testid={`batch-add-open-${b.id}`} variant="outline" onClick={() => { setAddFor(b.id); setItem(emptyItem); }} className="rounded-full h-9 border-dashed">
              <PlusCircle className="h-4 w-4 mr-1.5" /> Add product to batch
            </Button>
          )
        )}
        {live && <p className="text-[11px] text-gray-500 mt-1">You can edit product details while live. Duration and start time are fixed once live.</p>}
      </div>
    );
  };

  const draftBatches = batches.filter((b) => b.status === "draft");
  const liveBatches = batches.filter((b) => b.status === "live");
  const expiredBatches = batches.filter((b) => b.status === "expired");

  return (
    <div className="space-y-5" data-testid="vendor-live-tab">
      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1"><Radio className="h-4 w-4 text-red-600" /><div className="font-display text-xl">Live batches</div></div>
            <p className="text-xs text-gray-500">Bulk-create products in a draft batch — they stay hidden until you press Start Live. Everything in a batch goes live and expires together on one shared timer (0–240 hours). Sold-out items drop off the storefront automatically.</p>
          </div>
          <Button data-testid="batch-new" onClick={newBatch} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] h-9"><PlusCircle className="h-4 w-4 mr-1.5" /> New batch</Button>
        </div>
      </div>

      {liveBatches.length > 0 && <div className="space-y-3"><div className="text-xs uppercase tracking-widest text-green-700 font-semibold">Live now</div>{liveBatches.map((b) => renderBatchCard(b))}</div>}
      {draftBatches.length > 0 && <div className="space-y-3"><div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Drafts</div>{draftBatches.map((b) => renderBatchCard(b))}</div>}
      {expiredBatches.length > 0 && <div className="space-y-3"><div className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Expired</div>{expiredBatches.map((b) => renderBatchCard(b))}</div>}
      {batches.length === 0 && <div className="text-sm text-gray-400 text-center py-10 border border-dashed border-gray-200 rounded-2xl">No live batches yet. Create one to bulk-upload products for a timed showcase.</div>}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="batch-product-edit-dialog">
          <DialogHeader><DialogTitle>Edit product</DialogTitle></DialogHeader>
          {editing && (
            <>
              <ProductForm form={editForm} setForm={setEditForm} testid="batch-edit" cats={cats} vendorCat={vendorCat} keepSavedGst />
              <Button data-testid="batch-edit-save" onClick={saveEdit} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815] mt-3">Save changes</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
